import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePermission from '../hooks/usePermission';

/**
 * Where qa.rentfoxxy.com opens for each person (18 Oct 2026: the new UI is the
 * only UI). Their team's own page first — the field technician's phone, the
 * guard's gate, the floor, the support queue … — then the first new-UI page
 * their access opens, so nobody lands on a page they cannot use. The sections
 * are the same ones the route guards check (role_permissions matrix).
 */
const ROLE_HOME = {
  guard: [['guard_gate_checking', '/move/gate'], ['gate_dashboard', '/move/gate']],
  support_tech: [['support_tickets', '/serve/my-work'], ['technician_bucket', '/move/my-deliveries']],
  technician: [['technician_bucket', '/move/my-deliveries'], ['floor_tickets', '/produce/floor']],
  delivery: [['technician_bucket', '/move/my-deliveries']],
  delivery_technician: [['technician_bucket', '/move/my-deliveries']],
  field_technician: [['technician_bucket', '/move/my-deliveries'], ['support_tickets', '/serve/my-work']],
  support_lead: [['support_tickets', '/serve/queue']],
  team_member: [['floor_tickets', '/produce/floor'], ['floor_pipeline', '/produce/floor']],
  team_lead: [['floor_tickets', '/produce/floor'], ['floor_pipeline', '/produce/floor']],
  floor_manager: [['floor_pipeline', '/produce/floor'], ['floor_tickets', '/produce/floor']],
  qc: [['floor_tickets', '/produce/floor'], ['qc_management', '/produce/floor']],
  dispatch_qc: [['floor_tickets', '/produce/floor']],
  // 467: the picker's phone screen.
  picker: [['picker_tasks', '/produce/picker'], ['pending_inventory', '/produce/into-stock']],
  dispatch: [['delivery_challans', '/move/challans'], ['dispatch_pending_orders', '/move/orders-to-accept']],
  warehouse: [['delivery_challans', '/move/challans'], ['inventory_management', '/stock/ready']],
  procurement: [['vendor_management', '/procure/purchase-orders'], ['parts_procurement', '/procure/spare-parts-orders']],
  accounts: [['customer_billing', '/money/invoices'], ['vendor_billing_mgmt', '/money/vendor-bills']],
  sales: [['leads', '/sell/leads'], ['sales_orders_doc', '/sell/sales-orders'], ['sales_quotations', '/sell/quotations']],
};

// Anyone else (admin, manager, HR …), or a role whose own page is not granted:
// the first page they can open, the overview first.
const LANDING_ORDER = [
  ['billing_dashboard', '/home'],
  ['leads', '/sell/leads'],
  ['sales_orders_doc', '/sell/sales-orders'],
  ['sales_quotations', '/sell/quotations'],
  ['floor_pipeline', '/produce/floor'],
  ['floor_tickets', '/produce/floor'],
  ['support_tickets', '/serve/queue'],
  ['technician_bucket', '/move/my-deliveries'],
  ['delivery_challans', '/move/challans'],
  ['return_dc', '/move/return-challans'],
  ['guard_gate_checking', '/move/gate'],
  ['vendor_management', '/procure/purchase-orders'],
  ['parts_procurement', '/procure/spare-parts-orders'],
  ['parts_inventory', '/stock/parts'],
  ['inventory_management', '/stock/assets'],
  ['customer_inventory', '/stock/with-customers'],
  ['customer_billing', '/money/invoices'],
  ['vendor_billing_mgmt', '/money/vendor-bills'],
  ['customers', '/sell/customers'],
  ['demo_management', '/sell/demo'],
];

export default function HomeRedirect() {
  const { isAuthenticated, loading, user } = useAuth();
  const { canView } = usePermission();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  // A new joiner's first visit opens their training (Learn the flow, 18 Oct 2026);
  // after that, home is their work page and a banner reminds them until done.
  try {
    const key = `learn-intro-${user?.user_id || user?.id || ''}`;
    if (user && !localStorage.getItem(key)) {
      localStorage.setItem(key, '1');
      return <Navigate to="/learn?welcome=1" replace />;
    }
  } catch { /* storage blocked: go to work */ }
  const role = String(user?.role || '').toLowerCase();
  if (role === 'super_admin' || role === 'admin') return <Navigate to="/home" replace />;
  for (const [section, path] of ROLE_HOME[role] || []) {
    if (canView(section)) return <Navigate to={path} replace />;
  }
  for (const [section, path] of LANDING_ORDER) {
    if (canView(section)) return <Navigate to={path} replace />;
  }
  return <Navigate to="/home" replace />;
}
